import { getSupportChatCopy } from './copy';
import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { generateUUID } from '@crm/shared/uuidv7';
import {
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES,
  PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX,
} from '@crm/shared/public-marketplace';
import { ArrowUp, Check, MessageCircle, RotateCw, X } from 'lucide-react';
import { PublicSupportChatAnswerUnavailableError } from './types';
import type { SupportChatApi, PublicSupportChatMessage } from './types';
import { linkifySupportMessage } from './supportMessageLinks';
import { SUPPORT_CHAT_OPEN_EVENT } from './openSupportChat';

const SESSION_STORAGE_KEY = 'inmueble.public-support.session.v1';
const PENDING_STORAGE_KEY = 'inmueble.public-support.pending.v1';
const CONSENT_STORAGE_KEY = 'inmueble.public-support.consent.v1';
const MILLISECONDS_PER_MINUTE = 60_000;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const THREAD_REFRESH_INTERVAL_MS = MILLISECONDS_PER_MINUTE;
const THREAD_REFRESH_MAX_BACKOFF_MINUTES = 5;
const THREAD_REFRESH_MAX_BACKOFF_MS =
  THREAD_REFRESH_MAX_BACKOFF_MINUTES * MILLISECONDS_PER_MINUTE;
const THREAD_REFRESH_MAX_BACKOFF_EXPONENT = 3;
const TRANSFERRED_SESSION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const TRANSFERRED_SESSION_TOKEN_FRAGMENT_KEY = 'supportSession';
const TRANSFERRED_SESSION_EXPIRY_FRAGMENT_KEY = 'supportSessionExpiresAt';
const SENSITIVE_SUPPORT_CONTEXT_PATHS = [
  PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX,
  '/activar/',
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.resetPassword,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.resetPasswordLocalized,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.verifyEmail,
];

interface StoredSession {
  token: string;
  expiresAt: string;
}

interface PendingMessageRetry {
  clientMessageId: string;
  text: string;
  received?: boolean;
  canContinue?: boolean;
}

async function acknowledgeVisibleWebMessages(
  sessionToken: string,
  messages: PublicSupportChatMessage[],
  acknowledgePublicSupportChatMessages: SupportChatApi['acknowledgePublicSupportChatMessages']
): Promise<void> {
  const messageIds = Array.from(
    new Set(
      messages
        .filter(
          (message) =>
            message.direction === 'outbound' &&
            message.channel === 'web' &&
            message.deliveryStatus === 'sent'
        )
        .map((message) => message.id)
    )
  );
  if (messageIds.length === 0) return;
  try {
    await acknowledgePublicSupportChatMessages({ sessionToken, messageIds });
  } catch {
    // Delivery acknowledgement is retried after the next successful refresh.
  }
}

function storageGet(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // The conversation still works for the current page when storage is blocked.
  }
}

function storageRemove(key: string): void {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // There is no persisted value to remove when storage is blocked.
  }
}

function readStoredSession(sessionStorageKey: string): StoredSession | null {
  try {
    const raw = storageGet(sessionStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      new Date(parsed.expiresAt).getTime() <= Date.now()
    ) {
      storageRemove(sessionStorageKey);
      return null;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt };
  } catch {
    storageRemove(sessionStorageKey);
    return null;
  }
}

function readPendingMessage(token: string): PendingMessageRetry | null {
  try {
    const raw = storageGet(PENDING_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as {
      token?: string;
      clientMessageId?: string;
      text?: string;
      received?: boolean;
      canContinue?: boolean;
    };
    if (
      stored.token !== token ||
      typeof stored.clientMessageId !== 'string' ||
      typeof stored.text !== 'string' ||
      !stored.text.trim()
    )
      return null;
    return {
      clientMessageId: stored.clientMessageId,
      text: stored.text,
      received: stored.received === true,
      canContinue: stored.canContinue === true,
    };
  } catch {
    return null;
  }
}

function readTransferredSession(): StoredSession | null {
  const fragment = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const fragmentParams = new URLSearchParams(fragment);
  const token = fragmentParams.get(TRANSFERRED_SESSION_TOKEN_FRAGMENT_KEY);
  const expiresAt = fragmentParams.get(TRANSFERRED_SESSION_EXPIRY_FRAGMENT_KEY);
  if (token === null && expiresAt === null) return null;

  fragmentParams.delete(TRANSFERRED_SESSION_TOKEN_FRAGMENT_KEY);
  fragmentParams.delete(TRANSFERRED_SESSION_EXPIRY_FRAGMENT_KEY);
  const remainingFragment = fragmentParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${
      remainingFragment ? `#${remainingFragment}` : ''
    }`
  );

  if (
    token === null ||
    expiresAt === null ||
    !TRANSFERRED_SESSION_TOKEN_PATTERN.test(token)
  ) {
    return null;
  }
  const expiryTime = new Date(expiresAt).getTime();
  if (
    Number.isNaN(expiryTime) ||
    new Date(expiryTime).toISOString() !== expiresAt ||
    expiryTime <= Date.now()
  ) {
    return null;
  }

  const session = { token, expiresAt } satisfies StoredSession;
  return session;
}

function currentSupportPagePath(): string | null {
  const pathname = window.location.pathname;
  return SENSITIVE_SUPPORT_CONTEXT_PATHS.some((segment) =>
    pathname.includes(segment)
  )
    ? null
    : pathname;
}

function messageBody(body: string): ReactNode {
  return linkifySupportMessage(body).map((part, index) =>
    part.type === 'link' ? (
      <a
        key={`${part.href}-${index}`}
        href={part.href}
        target="_blank"
        rel="noreferrer"
      >
        {part.href}
      </a>
    ) : (
      part.value
    )
  );
}

export interface SupportChatProps {
  api: SupportChatApi;
  siteName: string;
  contactEmail: string;
  privacyUrl: string;
  formatDateTime: (value: string) => string;
  suspended?: boolean;
  onOpen?: () => void;
  includePageContext?: boolean;
  language?: string;
}

export function SupportChat({
  api,
  siteName,
  contactEmail,
  privacyUrl,
  formatDateTime,
  includePageContext = true,
  language = 'es',
  suspended = false,
  onOpen,
}: SupportChatProps): ReactNode {
  const copy = getSupportChatCopy(language);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const openPanel = (): void => {
    onOpenRef.current?.();
    setIsOpen(true);
  };
  const {
    createPublicSupportChatSession,
    getPublicSupportChatRetryAfterSeconds,
    getPublicSupportChatMessages,
    isPublicSupportChatSessionRejected,
    sendPublicSupportChatMessage,
  } = api;
  const acknowledgeMessages = (
    token: string,
    history: PublicSupportChatMessage[]
  ): Promise<void> =>
    acknowledgeVisibleWebMessages(
      token,
      history,
      api.acknowledgePublicSupportChatMessages
    );
  useEffect(() => {
    const openChat = (): void => {
      onOpenRef.current?.();
      setIsOpen(true);
    };
    window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, openChat);
    return () => window.removeEventListener(SUPPORT_CHAT_OPEN_EVENT, openChat);
  }, []);
  const [transferredSession] = useState<StoredSession | null>(() =>
    readTransferredSession()
  );
  const [isOpen, setIsOpen] = useState(transferredSession !== null);
  useEffect(() => {
    if (suspended) setIsOpen(false);
  }, [suspended]);
  const [hasConsent, setHasConsent] = useState(
    () =>
      transferredSession !== null ||
      storageGet(CONSENT_STORAGE_KEY) === 'accepted'
  );
  const [consentChecked, setConsentChecked] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [messages, setMessages] = useState<PublicSupportChatMessage[]>([]);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingMessageRetry, setPendingMessageRetry] =
    useState<PendingMessageRetry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionRetryAvailableAt, setSessionRetryAvailableAt] = useState<
    number | null
  >(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  const messageMutationVersionRef = useRef(0);

  const restorePending = (
    token: string,
    history: PublicSupportChatMessage[]
  ): void => {
    const pending = readPendingMessage(token);
    if (!pending) return;
    if (
      history.some(
        (message) => message.replyToClientMessageId === pending.clientMessageId
      )
    ) {
      storageRemove(PENDING_STORAGE_KEY);
      return;
    }
    const received =
      pending.received ||
      history.some(
        (message) => message.clientMessageId === pending.clientMessageId
      );
    setPendingMessageRetry({ ...pending, received });
    setText(received ? '' : pending.text);
    setError(received ? copy.answerFailure : copy.sendFailure);
  };

  useEffect(() => {
    if (!pendingMessageRetry || isSending) return;
    if (
      messages.some(
        (message) =>
          message.replyToClientMessageId === pendingMessageRetry.clientMessageId
      )
    ) {
      setPendingMessageRetry(null);
      storageRemove(PENDING_STORAGE_KEY);
      setText('');
      setError(null);
    }
  }, [messages, pendingMessageRetry, isSending]);

  const initializeSessionOnce = async (): Promise<void> => {
    if (
      sessionToken ||
      (sessionRetryAvailableAt !== null && sessionRetryAvailableAt > Date.now())
    )
      return;
    setIsLoading(true);
    setError(null);
    try {
      if (transferredSession) {
        try {
          const history = await getPublicSupportChatMessages(
            transferredSession.token
          );
          storageSet(SESSION_STORAGE_KEY, JSON.stringify(transferredSession));
          setSessionToken(transferredSession.token);
          setMessages(history);
          restorePending(transferredSession.token, history);
          void acknowledgeMessages(transferredSession.token, history);
          return;
        } catch (historyError) {
          if (!isPublicSupportChatSessionRejected(historyError)) {
            throw historyError;
          }
          if (storageGet(CONSENT_STORAGE_KEY) !== 'accepted') {
            setHasConsent(false);
            return;
          }
        }
      }
      const stored = readStoredSession(SESSION_STORAGE_KEY);
      if (stored) {
        try {
          const history = await getPublicSupportChatMessages(stored.token);
          setSessionToken(stored.token);
          setMessages(history);
          restorePending(stored.token, history);
          void acknowledgeMessages(stored.token, history);
          return;
        } catch (historyError) {
          if (!isPublicSupportChatSessionRejected(historyError)) {
            throw historyError;
          }
          storageRemove(SESSION_STORAGE_KEY);
        }
      }
      const session = await createPublicSupportChatSession();
      storageRemove(PENDING_STORAGE_KEY);
      setPendingMessageRetry(null);
      storageSet(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          token: session.sessionToken,
          expiresAt: session.expiresAt,
        } satisfies StoredSession)
      );
      setSessionToken(session.sessionToken);
      setMessages(session.messages);
      void acknowledgeMessages(session.sessionToken, session.messages);
    } catch (initializationError) {
      const retryAfterSeconds =
        getPublicSupportChatRetryAfterSeconds(initializationError);
      if (retryAfterSeconds !== null) {
        setSessionRetryAvailableAt(
          Date.now() + retryAfterSeconds * MILLISECONDS_PER_SECOND
        );
      }
      setError(
        initializationError instanceof Error
          ? retryAfterSeconds !== null
            ? copy.rateLimit.replace(
                '{minutes}',
                String(Math.ceil(retryAfterSeconds / SECONDS_PER_MINUTE))
              )
            : initializationError.message ===
                'El chat no está disponible en este momento.'
              ? copy.unavailable
              : initializationError.message
          : copy.unavailable
      );
    } finally {
      setIsLoading(false);
    }
  };

  const initializeSession = (): Promise<void> => {
    if (initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }
    const initialization = initializeSessionOnce().finally(() => {
      if (initializationPromiseRef.current === initialization) {
        initializationPromiseRef.current = null;
      }
    });
    initializationPromiseRef.current = initialization;
    return initialization;
  };

  useEffect(() => {
    if (isOpen && hasConsent) void initializeSession();
  }, [hasConsent, isOpen, sessionToken]);

  useEffect(() => {
    if (sessionRetryAvailableAt === null) return;
    const remainingMs = sessionRetryAvailableAt - Date.now();
    if (remainingMs <= 0) {
      setSessionRetryAvailableAt(null);
      return;
    }
    const timeout = window.setTimeout(
      () => setSessionRetryAvailableAt(null),
      remainingMs
    );
    return () => window.clearTimeout(timeout);
  }, [sessionRetryAvailableAt]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, isSending]);

  useEffect(() => {
    if (isOpen && sessionToken) inputRef.current?.focus();
  }, [isOpen, sessionToken]);

  useEffect(() => {
    if (!isOpen || !sessionToken) return undefined;
    let isCancelled = false;
    let refreshFailureCount = 0;
    let refreshTimeout: number | null = null;

    const scheduleRefresh = (delayMs: number): void => {
      if (isCancelled) return;
      if (refreshTimeout !== null) {
        window.clearTimeout(refreshTimeout);
      }
      refreshTimeout = window.setTimeout(() => void refresh(), delayMs);
    };

    const refresh = async (): Promise<void> => {
      let sessionWasRejected = false;
      const refreshMutationVersion = messageMutationVersionRef.current;
      if (document.visibilityState === 'hidden') {
        scheduleRefresh(THREAD_REFRESH_INTERVAL_MS);
        return;
      }
      try {
        const history = await getPublicSupportChatMessages(sessionToken);
        if (isCancelled) return;
        if (refreshMutationVersion === messageMutationVersionRef.current) {
          setMessages(history);
        }
        void acknowledgeMessages(sessionToken, history);
        refreshFailureCount = 0;
      } catch (refreshError) {
        if (isPublicSupportChatSessionRejected(refreshError)) {
          sessionWasRejected = true;
          storageRemove(SESSION_STORAGE_KEY);
          storageRemove(PENDING_STORAGE_KEY);
          setSessionToken(null);
          setMessages([]);
          setPendingMessageRetry(null);
          setError(copy.expired);
          return;
        }
        refreshFailureCount += 1;
      } finally {
        if (!isCancelled && !sessionWasRejected) {
          const backoffMultiplier =
            2 **
            Math.min(refreshFailureCount, THREAD_REFRESH_MAX_BACKOFF_EXPONENT);
          scheduleRefresh(
            Math.min(
              THREAD_REFRESH_INTERVAL_MS * backoffMultiplier,
              THREAD_REFRESH_MAX_BACKOFF_MS
            )
          );
        }
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh(0);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleRefresh(THREAD_REFRESH_INTERVAL_MS);
    return () => {
      isCancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimeout !== null) {
        window.clearTimeout(refreshTimeout);
      }
    };
  }, [isOpen, sessionToken]);

  const acceptConsent = (): void => {
    if (!consentChecked) return;
    storageSet(CONSENT_STORAGE_KEY, 'accepted');
    setHasConsent(true);
  };

  const submitMessage = async (
    event?: FormEvent,
    retryPending = false
  ): Promise<void> => {
    event?.preventDefault();
    const trimmed = text.trim();
    if ((!trimmed && !pendingMessageRetry) || !sessionToken || isSending)
      return;
    const pendingMessage =
      !retryPending && pendingMessageRetry?.canContinue && trimmed
        ? null
        : pendingMessageRetry;
    const clientMessageId = pendingMessage?.clientMessageId ?? generateUUID();
    const messageText = pendingMessage?.text ?? trimmed;
    messageMutationVersionRef.current += 1;
    const optimistic: PublicSupportChatMessage = {
      id: clientMessageId,
      direction: 'inbound',
      channel: 'web',
      body: messageText,
      senderType: 'client',
      deliveryStatus: 'pending',
      clientMessageId,
      createdAt: new Date().toISOString(),
    };
    setMessages((current) =>
      current.some((message) => message.clientMessageId === clientMessageId)
        ? current
        : [...current, optimistic]
    );
    setText('');
    setIsSending(true);
    setError(null);
    storageSet(
      PENDING_STORAGE_KEY,
      JSON.stringify({
        token: sessionToken,
        clientMessageId,
        text: messageText,
      })
    );
    try {
      const response = await sendPublicSupportChatMessage({
        sessionToken,
        clientMessageId,
        text: messageText,
        pagePath: includePageContext ? currentSupportPagePath() : null,
      });
      setMessages((current) => [
        ...current.filter(
          (message) =>
            message.id !== clientMessageId &&
            message.id !== response.inbound.id &&
            message.id !== response.outbound?.id
        ),
        response.inbound,
        ...(response.outbound ? [response.outbound] : []),
      ]);
      if (response.outbound) {
        void acknowledgeMessages(sessionToken, [response.outbound]);
      }
      if (response.outbound) {
        setPendingMessageRetry(null);
        storageRemove(PENDING_STORAGE_KEY);
      } else {
        const pending = {
          clientMessageId,
          text: messageText,
          received: true,
          canContinue: true,
        };
        setPendingMessageRetry(pending);
        storageSet(
          PENDING_STORAGE_KEY,
          JSON.stringify({ token: sessionToken, ...pending })
        );
        setError(copy.reviewPending);
      }
    } catch (sendError) {
      const received =
        sendError instanceof PublicSupportChatAnswerUnavailableError
          ? sendError.inbound
          : null;
      setMessages((current) => [
        ...current.filter(
          (message) =>
            message.id !== clientMessageId && message.id !== received?.id
        ),
        ...(received ? [received] : []),
      ]);
      setText(received ? '' : messageText);
      if (isPublicSupportChatSessionRejected(sendError)) {
        storageRemove(SESSION_STORAGE_KEY);
        storageRemove(PENDING_STORAGE_KEY);
        setSessionToken(null);
        setPendingMessageRetry(null);
      } else {
        const pending = {
          clientMessageId,
          text: messageText,
          received: received !== null,
          canContinue:
            sendError instanceof PublicSupportChatAnswerUnavailableError &&
            sendError.canContinue,
        };
        setPendingMessageRetry(pending);
        storageSet(
          PENDING_STORAGE_KEY,
          JSON.stringify({ token: sessionToken, ...pending })
        );
      }
      setError(
        received
          ? copy.answerFailure
          : sendError instanceof Error
            ? sendError.message ===
              'No pudimos enviar el mensaje. Intenta nuevamente.'
              ? copy.sendFailure
              : sendError.message
            : copy.sendFailure
      );
    } finally {
      messageMutationVersionRef.current += 1;
      setIsSending(false);
    }
  };

  const handleInputKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submitMessage();
    }
  };

  return (
    <div className="public-support-chat">
      {isOpen ? (
        <section
          className="public-support-chat__panel"
          role="dialog"
          aria-label={copy.dialog.replace('{site}', siteName)}
        >
          <header className="public-support-chat__header">
            <div className="public-support-chat__identity">
              <span className="public-support-chat__mark" aria-hidden="true">
                <MessageCircle size={18} />
              </span>
              <div>
                <strong>{copy.dialog.replace('{site}', siteName)}</strong>
                <span>{copy.subtitle.replace('{site}', siteName)}</span>
              </div>
            </div>
            <button
              type="button"
              className="public-support-chat__icon-button"
              onClick={() => setIsOpen(false)}
              aria-label={copy.close}
            >
              <X size={20} />
            </button>
          </header>

          {!hasConsent ? (
            <div className="public-support-chat__consent">
              <span
                className="public-support-chat__consent-icon"
                aria-hidden="true"
              >
                <MessageCircle size={28} />
              </span>
              <h2>{copy.welcome.replace('{site}', siteName)}</h2>
              <p>{copy.description.replace('{site}', siteName)}</p>
              <label>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(event) => setConsentChecked(event.target.checked)}
                />
                <span>
                  {copy.consent}{' '}
                  <a href={privacyUrl} target="_blank" rel="noreferrer">
                    {copy.privacy}
                  </a>
                  .
                </span>
              </label>
              <button
                type="button"
                className="public-support-chat__primary-button"
                disabled={!consentChecked}
                onClick={acceptConsent}
              >
                {copy.start}
              </button>
            </div>
          ) : (
            <>
              <div className="public-support-chat__messages" aria-live="polite">
                {isLoading ? (
                  <div className="public-support-chat__status">
                    <RotateCw className="public-support-chat__spin" size={20} />
                    {copy.recovering}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="public-support-chat__empty">
                    <span aria-hidden="true">
                      <Check size={18} />
                    </span>
                    <h2>{copy.ready}</h2>
                    <p>{copy.empty.replace('{site}', siteName)}</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <article
                      key={message.id}
                      className={`public-support-chat__message public-support-chat__message--${message.direction}`}
                    >
                      <div>{messageBody(message.body)}</div>
                      {message.channel === 'whatsapp' ? (
                        <span className="public-support-chat__channel">
                          WhatsApp
                        </span>
                      ) : null}
                      <time dateTime={message.createdAt}>
                        {formatDateTime(message.createdAt)}
                      </time>
                    </article>
                  ))
                )}
                {isSending ? (
                  <div
                    className="public-support-chat__typing"
                    aria-label={copy.typing}
                  >
                    <span />
                    <span />
                    <span />
                  </div>
                ) : null}
                <div ref={messageEndRef} />
              </div>
              {error ? (
                <div className="public-support-chat__error" role="alert">
                  {error} <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                  {pendingMessageRetry && !isSending ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void submitMessage(undefined, true)}
                      >
                        {copy.retryAnswer}
                      </button>
                      {pendingMessageRetry.canContinue ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPendingMessageRetry(null);
                            storageRemove(PENDING_STORAGE_KEY);
                            setText('');
                            setError(null);
                            inputRef.current?.focus();
                          }}
                        >
                          {copy.continueConversation}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                  {!sessionToken &&
                  !isLoading &&
                  sessionRetryAvailableAt === null ? (
                    <button
                      type="button"
                      onClick={() => void initializeSession()}
                    >
                      {copy.retry}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <form
                className="public-support-chat__composer"
                onSubmit={submitMessage}
              >
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  maxLength={4000}
                  rows={1}
                  placeholder={copy.placeholder}
                  aria-label={copy.message}
                  readOnly={
                    pendingMessageRetry !== null &&
                    !pendingMessageRetry.canContinue
                  }
                  disabled={!sessionToken || isLoading || isSending}
                />
                <button
                  type="submit"
                  disabled={
                    (!text.trim() && !pendingMessageRetry) ||
                    !sessionToken ||
                    isLoading ||
                    isSending
                  }
                  aria-label={copy.send}
                >
                  <ArrowUp size={19} />
                </button>
              </form>
              <footer>{copy.disclosure}</footer>
            </>
          )}
        </section>
      ) : (
        <button
          type="button"
          className="public-support-chat__launcher"
          onClick={openPanel}
          aria-label={copy.open.replace('{site}', siteName)}
        >
          <MessageCircle size={21} />
          <span>{copy.launcher}</span>
        </button>
      )}
    </div>
  );
}
